import { createSlice } from "@reduxjs/toolkit";
import {
  AccountFundAccessResponse,
  ContactResponse,
  UserRole,
  UserIFC,
  ProfileAuditResponse,
  Country,
} from "@myProfile/models";
import {
  fetchAccountFundAccessThunk,
  fetchContactInformationThunk,
  fetchUserRolesThunk,
  fetchUserIFCsThunk,
  fetchProfileAuditThunk,
  saveProfileThunk,
  fetchCountriesThunk,
} from "./thunks";

//users roles state interface
export interface UserRolesState {
  data: UserRole[] | null;
  loading: boolean;
  error: string | null;
}

// User IFCs (Investor Fund Contacts) state interface
// Used to determine profile menu visibility based on user's fund assignments
export interface UserIFCsState {
  data: UserIFC[] | null;
  loading: boolean;
  error: string | null;
}

export interface AccountFundAccessState {
  data: AccountFundAccessResponse | null;
  loading: boolean;
  error: string | null;
  resetPasswordDialogOpen: boolean;
  resetMFADialogOpen: boolean;
  resetMFAEmail: string | null;
  userRoles: UserRolesState;
  userIFCs: UserIFCsState;
}

export interface CountriesState {
  data: Country[];
  loading: boolean;
  error: string | null;
}

export interface ProfileAuditState {
  data: ProfileAuditResponse | null;
  loading: boolean;
  error: string | null;
  fetched: boolean; // Track if API has been called (regardless of result)
}

export interface SaveProfileState {
  loading: boolean;
  error: string | null;
  lastResponse: { success: boolean; emailChangeRequested: boolean } | null;
}

export interface ContactInformationState {
  contactInformation: ContactResponse | null;
  contactLoading: boolean;
  contactError: string | null;
  countries: CountriesState;
  profileAudit: ProfileAuditState;
  saveProfile: SaveProfileState;
}

const initialState: AccountFundAccessState = {
  data: null,
  loading: false,
  error: null,
  resetPasswordDialogOpen: false,
  userRoles: {
    data: null,
    loading: false,
    error: null,
  },
  userIFCs: {
    data: null,
    loading: false,
    error: null,
  },
  resetMFADialogOpen: false,
  resetMFAEmail: null,
};

const accountFundAccessSlice = createSlice({
  name: "accountFundAccess",
  initialState,
  reducers: {
    resetAccountFundAccessState: () => initialState,
    openResetPasswordDialog: (state) => {
      state.resetPasswordDialogOpen = true;
    },
    closeResetPasswordDialog: (state) => {
      state.resetPasswordDialogOpen = false;
    },
    openResetMFADialog: (state, { payload }: { payload?: string }) => {
      state.resetMFADialogOpen = true;
      state.resetMFAEmail = payload ?? null;
    },
    closeResetMFADialog: (state) => {
      state.resetMFADialogOpen = false;
      state.resetMFAEmail = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Account/Fund Access thunk handlers
      .addCase(fetchAccountFundAccessThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAccountFundAccessThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.error = null;
      })
      .addCase(fetchAccountFundAccessThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // User Roles thunk handlers
      .addCase(fetchUserRolesThunk.pending, (state) => {
        state.userRoles.loading = true;
        state.userRoles.error = null;
      })
      .addCase(fetchUserRolesThunk.fulfilled, (state, action) => {
        state.userRoles.loading = false;
        state.userRoles.data = action.payload;
        state.userRoles.error = null;
      })
      .addCase(fetchUserRolesThunk.rejected, (state, action) => {
        state.userRoles.loading = false;
        state.userRoles.error = action.payload as string;
      })
      // User IFCs thunk handlers
      .addCase(fetchUserIFCsThunk.pending, (state) => {
        state.userIFCs.loading = true;
        state.userIFCs.error = null;
      })
      .addCase(fetchUserIFCsThunk.fulfilled, (state, action) => {
        state.userIFCs.loading = false;
        state.userIFCs.data = action.payload;
        state.userIFCs.error = null;
      })
      .addCase(fetchUserIFCsThunk.rejected, (state, action) => {
        state.userIFCs.loading = false;
        state.userIFCs.error = action.payload as string;
      });
  },
});

export const {
  resetAccountFundAccessState,
  openResetPasswordDialog,
  closeResetPasswordDialog,
  openResetMFADialog,
  closeResetMFADialog,
} = accountFundAccessSlice.actions;
export default accountFundAccessSlice.reducer;

// Contact Information Slice
const contactInformationInitialState: ContactInformationState = {
  contactInformation: null,
  contactLoading: false,
  contactError: null,
  countries: {
    data: [],
    loading: false,
    error: null,
  },
  profileAudit: {
    data: null,
    loading: false,
    error: null,
    fetched: false,
  },
  saveProfile: {
    loading: false,
    error: null,
    lastResponse: null,
  },
};

const contactInformationSlice = createSlice({
  name: "contactInformation",
  initialState: contactInformationInitialState,
  reducers: {
    resetContactInformationState: () => contactInformationInitialState,
    clearSaveProfileResponse: (state) => {
      state.saveProfile.lastResponse = null;
    },
    resetProfileAuditFetched: (state) => {
      state.profileAudit.fetched = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Contact Information thunk handlers
      .addCase(fetchContactInformationThunk.pending, (state) => {
        state.contactLoading = true;
        state.contactError = null;
      })
      .addCase(fetchContactInformationThunk.fulfilled, (state, action) => {
        state.contactLoading = false;
        state.contactInformation = action.payload;
        state.contactError = null;
      })
      .addCase(fetchContactInformationThunk.rejected, (state, action) => {
        state.contactLoading = false;
        state.contactError = action.payload as string;
      })
      // Countries thunk handlers
      .addCase(fetchCountriesThunk.pending, (state) => {
        state.countries.loading = true;
        state.countries.error = null;
      })
      .addCase(fetchCountriesThunk.fulfilled, (state, action) => {
        state.countries.loading = false;
        state.countries.data = action.payload;
        state.countries.error = null;
      })
      .addCase(fetchCountriesThunk.rejected, (state, action) => {
        state.countries.loading = false;
        state.countries.error = (action.payload as string) || "Failed to load countries";
      })
      // Profile Audit thunk handlers
      .addCase(fetchProfileAuditThunk.pending, (state) => {
        state.profileAudit.loading = true;
        state.profileAudit.error = null;
      })
      .addCase(fetchProfileAuditThunk.fulfilled, (state, action) => {
        state.profileAudit.loading = false;
        state.profileAudit.data = action.payload;
        state.profileAudit.error = null;
        state.profileAudit.fetched = true;
      })
      .addCase(fetchProfileAuditThunk.rejected, (state, action) => {
        state.profileAudit.loading = false;
        state.profileAudit.error = action.payload as string;
        state.profileAudit.fetched = true;
      })
      // Save Profile thunk handlers
      .addCase(saveProfileThunk.pending, (state) => {
        state.saveProfile.loading = true;
        state.saveProfile.error = null;
      })
      .addCase(saveProfileThunk.fulfilled, (state, action) => {
        state.saveProfile.loading = false;
        state.saveProfile.lastResponse = {
          success: action.payload.success,
          emailChangeRequested: action.payload.emailChangeRequested,
        };
        state.saveProfile.error = null;
      })
      .addCase(saveProfileThunk.rejected, (state, action) => {
        state.saveProfile.loading = false;
        state.saveProfile.error = action.payload as string;
      });
  },
});

export const { resetContactInformationState, clearSaveProfileResponse, resetProfileAuditFetched } =
  contactInformationSlice.actions;
export const contactInformationReducer = contactInformationSlice.reducer;
